import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { LineItemProps } from './LineItem'
import { Stack, Text } from '@fluentui/react'
import { Badge, Tooltip } from '@fluentui/react-components'
import { InvoiceListItemDto, InvoiceType, InvoiceTypeMap } from '../../../../../config/finance/invoiceConfig'
import FormattedDateBadge from '../../../../Date/FormattedDateBadge'
import { MoneyRegular } from '@fluentui/react-icons'
import MoneyText from '../../../../Text/Currency/MoneyText'
import { formatNumber, parseNumber } from '../../../../../helpers/numberHelper'
import { formatPriceVat } from '../../../../../helpers/priceHelper'
import { PricePrecedence } from '../../../../../config/finance/financeConfig'
import useCurrencies from '../../../../../hooks/useCurrencies'
import useVatTypes from '../../../../../hooks/useVatTypes'
import { useTranslation } from 'react-i18next'

export default function InvoiceLineItem({ entity }: LineItemProps<InvoiceListItemDto>): ReactElement {
    const { getCurrency } = useCurrencies()
    const { getVatType } = useVatTypes()
    const { t: translate } = useTranslation()

    const isDepositInvoice = useMemo(() => entity.type === InvoiceType.Deposit, [entity])

    const unsettledPrice = useMemo(() => {
        //TODO HAM-1367 subtract settled price of this lineitem when we are editing an invoice.
        const unsettledPrice = entity.unsettledPrice
            ? formatPriceVat(
                  parseNumber(entity.unsettledPrice.total, 'en'),
                  getVatType(entity.unsettledPrice.vat?.[0]?.type),
                  getCurrency(entity.unsettledPrice.currency),
                  PricePrecedence.Total,
              )
            : { subtotal: 0, vat: [], total: 0 }
        const price = formatPriceVat(
            parseNumber(entity.price.total, 'en'),
            getVatType(entity.price.vat?.[0]?.type),
            getCurrency(entity.price.currency),
            PricePrecedence.Total,
        )

        return unsettledPrice || price
    }, [entity])

    return (
        <Stack horizontal={false} verticalAlign="center" tokens={{ childrenGap: 's2 s1', padding: 's2 0' }} wrap>
            <Text>{entity.number}</Text>
            <Stack horizontal tokens={{ childrenGap: 's2' }}>
                <Badge size="medium" appearance="tint">
                    {InvoiceTypeMap.get(entity.type)}
                </Badge>
                <FormattedDateBadge date={entity.date} />
                {isDepositInvoice && (
                    <>
                        <Tooltip content={translate('invoice.edit.form.invoiceline.unsettled.subtotal')} relationship="description" withArrow>
                            <Badge size="medium" icon={<MoneyRegular />} appearance="tint">
                                <MoneyText
                                    value={{
                                        currency: entity.price.currency,
                                        amount: formatNumber(unsettledPrice.subtotal, '0.00', 'en'),
                                    }}
                                    textProps={{ size: 200 }}
                                />
                            </Badge>
                        </Tooltip>
                        <Tooltip content={translate('invoice.edit.form.invoiceline.unsettled.total')} relationship="description" withArrow>
                            <Badge size="medium" icon={<MoneyRegular />} appearance="tint">
                                <MoneyText
                                    value={{
                                        currency: entity.price.currency,
                                        amount: formatNumber(unsettledPrice.total, '0.00', 'en'),
                                    }}
                                    textProps={{ size: 200 }}
                                />
                            </Badge>
                        </Tooltip>
                    </>
                )}
            </Stack>
        </Stack>
    )
}
